import * as React from 'react';
import * as cx from 'classnames';
import { noDefault } from 'lib/eventWrappers';
import SuperCritical from 'lib/supercritical';
import { TopicCardQuestionType } from 'server/enums/TopicCardQuestionType';
import {
    TopicCardTemplates,
    TopicCardTemplate,
    TopicCardTemplateOptionSlot,
    TopicCardTemplateUserAssignment,
    TopicCardTemplateAttachment
} from './model';
import { DownloadLink } from 'portcall/downloadlink/view';
import { OrgHeaderView } from 'portcall/orgprofile/orgheaderview';
import { PCAutoScroll } from 'portcall/pcautoscroll/view';
import { ToggleButton } from 'portcall/togglebutton/view';
import { ValidationErrorView } from 'core/validationerror/view';
import { UserSelectorView } from 'portcall/userselector/view';
import { UploadFileView } from 'portcall/uploadfile/view';

export const TopicCardTemplatesView = SuperCritical(({ model: m } : { model : TopicCardTemplates }) =>
    <div className="topiccardtemplates pc-page-fixedwidth">
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href={'#!orgprofile?id=' + m.Model.Id()}>&lt;&lt; Back to Organization</a>
        </div>
        <OrgHeaderView organization={m.Model} />
        <PageBody model={m} />
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : TopicCardTemplates }) =>
    <div className="pc-card page-body">
        <div className="pc-card-header">
            <a href="" className="add-card-link" onClick={noDefault(m.AddTemplate)}>Add a new card</a>
            <span className="pc-card-title">Topic Cards</span>
        </div>
        <TemplateList model={m} />
        <SelectedTemplateView model={m} />
    </div>
);

let TemplateList = SuperCritical(({ model: m } : { model : TopicCardTemplates }) =>
    <div className="template-list">
        {m.TopicCardTemplates().map(t => 
            <TemplateListItem model={m} template={t} key={t.Model.Id()} />
        )}
    </div>
);

let TemplateListItem = SuperCritical(({ model: m, template: t } : { model : TopicCardTemplates, template : TopicCardTemplate }) =>
    <PCAutoScroll className={cx('pc-topic-card-list-item', {
            selected: m.SelectedTemplate() === t
        })}
        onClick={() => m.SelectedTemplateId(t.Model.Id())}
        keepInView={() => m.SelectedTemplate() === t}
        retriggerScroll={m.TopicCardTemplates/* retrigger auto-scroll if template sort changes */}>
        <div className="card-title">{t.Model.EffectiveTitle()}</div>
        <div className="card-remarks">{t.Model.EffectiveRemarks() || '\u00A0'/* &nbsp; */}</div>
    </PCAutoScroll>
);

let SelectedTemplateView = SuperCritical(({ model: m } : { model : TopicCardTemplates }) => {
    const t = m.SelectedTemplate();
    if (!t) return null;
    return (
        <div className="selected-template-edit">
            <TitleAndAmenity template={t} />
            <QuestionAndQuestionType template={t} />
            <TimeEntryView template={t} />
            <PeopleView template={t} />
            <AttachmentsView template={t} />
            <RemarksView template={t} />
            <div className="delete-card-container">
                <a href="" onClick={noDefault(t.Delete)}>Delete Card</a>
            </div>
        </div>
    );
});

let TitleAndAmenity = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <TitleEdit template={t} />
            <AmenityEdit template={t} />
        </div>
        <div className="template-row-description-column">
            Enter the title of the topic card. If you link a card to an amenity, the amenity name
            will automatically be used for the title and will be un-editable.
        </div>
    </div>
);

let TitleEdit = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-title">
        <input className="template-edit-control"
            value={t.Model.EffectiveTitle()}
            onChange={e => t.Model.Title(e.currentTarget.value)}
            disabled={t.Model.AmenityTypeId() !== null} />
        <ValidationErrorView error={() => t.Validation()['Title']} />
    </div>
);

let AmenityEdit = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-amenity-type">
        {t.AmenityLink.ShowDropdown() ?
            <div>
                <div className="amenity-dropdown-container">
                    <select className="pc-select template-edit-control"
                        value={t.Model.AmenityTypeId() === null ? '' : '' + t.Model.AmenityTypeId()}
                        onChange={e => t.Model.AmenityTypeId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}>
                        <option value="">--</option>
                        {t.AmenityLink.AvailableAmenityTypes().map(a => 
                            <option value={a.Id()} key={a.Id()}>{a.Name()}</option>
                        )}
                    </select>
                    <span className="pc-select-chevron"></span>
                </div>
                <div className="undo-amenity-link-container">
                    <a href="" onClick={noDefault(t.AmenityLink.UndoAmenity)}>Undo Amenity</a>
                </div>
            </div>
          : <div className="show-amenity-link-container">
                <a href="" onClick={noDefault(() => t.AmenityLink.ShowDropdown(true))}>+Choose topic from Amenity List</a>
            </div>
        }
    </div>
);

let QuestionAndQuestionType = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <Question template={t} />
            <QuestionType template={t} />
        </div>
        <div className="template-row-description-column">
            Ask a question and select the answer type below.
            The answer indicated will be the default answer on all cards.
        </div>
    </div>
);

let Question = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-question">
        <textarea className="template-edit-control"
            value={t.Model.Question()}
            onChange={e => t.Model.Question(e.currentTarget.value)} />
        <ValidationErrorView error={() => t.Validation()['Question']} />
    </div>
);

let QuestionType = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-question-type">
        <div className="template-edit-separator"></div>
        <div className="template-question-type-select">
            <label className="question-type-section-label pc-text-smalllabel">answer</label>
            <QuestionTypeRadio template={t} questionType={TopicCardQuestionType.YesNo} label="yes/no" />
            <QuestionTypeRadio template={t} questionType={TopicCardQuestionType.MultipleChoice} label="multiple choice" />
        </div>
        <div className="template-question-answer-choices">
            {t.Model.QuestionType() === TopicCardQuestionType.YesNo &&
                <ToggleButton checked={() => false} onLabel="Yes" offLabel="No" disabled={true} />
            }
            {t.Model.QuestionType() === TopicCardQuestionType.MultipleChoice &&
                <CardOptionsEdit template={t} />
            }
        </div>
    </div>
);

let QuestionTypeRadio = SuperCritical(({ template: t, questionType, label } : { template : TopicCardTemplate, questionType : TopicCardQuestionType, label : string }) =>
    <label className="pc-radio pc-text-tinylabel question-type-radio">
        <input type="radio"
            checked={t.Model.QuestionType() === questionType}
            onChange={e => e.currentTarget.checked && t.Model.QuestionType(questionType)} />
        <span className="pc-radio-button"></span>
        {' '}
        <span className="pc-text-tinylabel">{label}</span>
    </label>
);

let CardOptionsEdit = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-card-options">
        {t.Options.Slots().map((slot, idx) =>
            <CardOptionSlotView slot={slot} template={t} isFirst={idx === 0} key={slot.SlotId} />
        )}
    </div>
);

let CardOptionSlotView = SuperCritical(({ slot: s, template: t, isFirst } : { slot : TopicCardTemplateOptionSlot, template : TopicCardTemplate, isFirst : boolean }) =>
    <div className="template-card-option-slot">
        <div>
            <span className="pc-radio option-radio">
                <input type="radio" checked={isFirst} disabled={true} />
                <span className="pc-radio-button"></span>
            </span>
            {' '}
            <input className="template-edit-control option-text"
                placeholder={isFirst ? 'default answer...' : 'answer...'}
                value={s.Model.Text()}
                onChange={e => s.Model.Text(e.currentTarget.value)} />
            {' '}
            {s.IsPersisted() && <a href="" className="pc-close-icon" onClick={noDefault(() => t.Options.DeleteSlot(s))}></a>}
        </div>
        <ValidationErrorView error={() => s.Validation()['Text']} />
    </div>
);

let TimeEntryView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <div className="template-time-entry">
                <div className="template-edit-separator"></div>
                <HasTimeEntryView template={t} />
                <TimeEntryLabelView template={t} />
            </div>
        </div>
        <div className="template-row-description-column">
            Indicate if this card has a time associated with it.
            Times are editable on individual cards.
        </div>
    </div>
);

let HasTimeEntryView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div>
        <label className="time-entry-section-label pc-text-smalllabel">time</label>
        <ToggleButton checked={t.Model.TimeEntry.HasTimeEntry} onLabel="Yes" offLabel="No" />
    </div>
);

let TimeEntryLabelView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) => {
    if (!t.Model.TimeEntry.HasTimeEntry()) return null;
    return (
        <div className="time-entry-label-edit">
            <input className="template-edit-control"
                value={t.Model.TimeEntry.Label() || ''}
                onChange={e => t.Model.TimeEntry.Label(e.currentTarget.value)} />
        </div>
    );
});

let PeopleView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <div className="template-people">
                <div className="template-edit-separator"></div>
                <div>
                    <label className="pc-text-smalllabel">people</label>
                </div>
                <div className="template-people-list">
                    {t.People.People().map(ua =>
                        <UserAssignmentView userAssignment={ua} key={ua.Model.UserId()} />
                    )}
                </div>
                <UserSelectorView className="template-edit-control" model={t.People.UserSelector} placeholder="Add a person" />
            </div>
        </div>
        <div className="template-row-description-column">
            Add people to a card to receive notifications on updates and changes to a card.
        </div>
    </div>
);

let UserAssignmentView = SuperCritical(({ userAssignment: u } : { userAssignment : TopicCardTemplateUserAssignment }) =>
    <div>
        <span className="pc-text-fixedwidth user-name">{u.Model.User() && u.Model.User()!.FullName()}</span>
        <a href="" className="pc-close-icon" onClick={noDefault(u.Delete)}></a>
    </div>
);

let AttachmentsView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <div className="template-attachments">
                <div className="template-edit-separator"></div>
                <div>
                    <label className="pc-text-smalllabel">attachments</label>
                </div>
                <div className="template-attachments-list">
                    {t.Attachments.Attachments().map(a =>
                        <TemplateAttachmentView attachment={a} key={a.Model.Id()} />
                    )}
                </div>
                <div>
                    <label className="add-attachment-link"
                        htmlFor={t.Attachments.UploadFile.FileInputId()}>
                        <a>+ Add Attachment</a>
                    </label>
                    <UploadFileView model={t.Attachments.UploadFile} />
                </div>
            </div>
        </div>
        <div className="template-row-description-column">
            Add attachments to a card for people to download and re-upload.
        </div>
    </div>
);

let TemplateAttachmentView = SuperCritical(({ attachment: a } : { attachment : TopicCardTemplateAttachment }) =>
    <div>
        <DownloadLink href={a.Model.AttachmentUrl()} className="attachment-icon fa fa-paperclip"></DownloadLink>
        <DownloadLink href={a.Model.AttachmentUrl()} className="attachment-filename pc-text-fixedwidth">{a.Model.FileName()}</DownloadLink>
        <a href="" className="pc-close-icon" onClick={noDefault(a.Delete)}></a>
    </div>
);

let RemarksView = SuperCritical(({ template: t } : { template : TopicCardTemplate }) =>
    <div className="template-row">
        <div className="template-row-edit-column">
            <div className="template-remarks">
                <div className="template-edit-separator"></div>
                <div className="template-remarks-section-label-container">
                    <label className="pc-text-smalllabel">remarks</label>
                </div>
                <div>
                    <textarea className="template-edit-control"
                        value={t.Model.EffectiveRemarks() || ''}
                        onChange={e => t.Model.Remarks(e.currentTarget.value)}
                        disabled={t.Model.AmenityTypeId() !== null} />
                </div>
            </div>
        </div>
        <div className="template-row-description-column">
            Enter any remarks or details about this card.
            If you link a card to an amenity, the amenity description will automatically be
            used for the remarks and will be un-editable.
        </div>
    </div>
);


// WEBPACK FOOTER //
// ./apps/portcall/topiccardtemplates/view.tsx